import React, { useEffect, useState } from 'react';
import * as Dialog from '@radix-ui/react-dialog';
import { css } from '@emotion/react';
import CloseSVG from '@birdi/icons/close.svg';
import WarningSVG from '@birdi/icons/warning.svg';
import { closeButtonCSS, contentCSS, portalCSS } from '@birdi/modal-kit';
import { useForm, Controller } from 'react-hook-form';
import { BirdiTheme, buttonBlue, buttonGrey, blocks } from '@birdi/theme';
import {
  stroot,
  auth,
  organisation as organisationAPI,
  user,
} from '@birdi/js-sdk';
import WorkspaceSelect from '@birdi/select/WorkspaceSelect';
import { useMetaSelector } from '../meta/reducer';

interface DeleteWorkspaceModalProps {
  open: boolean;
  setOpen: (value: boolean) => void;
  id: string;
}

interface SelectedWorkspace {
  label: string;
  value: string;
}

export const buttonsHolderStyle = css`
  display: flex;
  flex-direction: row;
  align-items: center;
  justify-content: center;
  margin: 2em 0 0 0;
  gap: 0.5rem;
`;
export const cancelButtonstyle = (theme: BirdiTheme) => css`
  ${buttonBlue(theme)}
  height: 2.5rem;
  width: 7rem;
`;
export const confirmButtonstyle = (theme: BirdiTheme) => css`
  ${buttonGrey(theme)}
  height: 2.5rem;
  min-width: 7rem;
  background: ${theme.mono0};
  color: ${theme.deepOcean};
  border: solid 1px ${theme.deepOcean};
`;

export const DeleteWorkspaceModal: React.FC<DeleteWorkspaceModalProps> = ({
  open,
  setOpen,
  id,
}) => {
  const meta = useMetaSelector();

  const [haveUsersOrMaps, setHaveUsersOrMaps] = useState(false);
  const [selectedWorkspace, setSelectedWorkspace] =
    useState<SelectedWorkspace | null>(null);

  useEffect(() => {
    organisationAPI.getUsersAndMapsCount(stroot('esei6u'), id).then((res) => {
      const { missionCount, mapCount, userCount } = res.body;
      if (missionCount + mapCount + userCount > 1) {
        // there is always at least 1 user how is the creator
        setHaveUsersOrMaps(true);
      }
    });
  }, []);

  const confirm = async () => {
    if ((haveUsersOrMaps && selectedWorkspace?.value) || !haveUsersOrMaps) {
      const alternativeWorkspace =
        selectedWorkspace?.value || meta.organisation.id;
      await organisationAPI.deleteWorkspace(
        stroot('ges0ze'),
        id,
        alternativeWorkspace,
      );
      window.location.reload();
    } else {
      // error
    }
  };
  const cancel = () => {
    setOpen(false);
  };

  return (
    <Dialog.Root open={open} onOpenChange={setOpen}>
      <Dialog.Portal>
        <Dialog.Overlay />

        <div css={portalCSS}>
          <Dialog.Content
            css={(theme) => css`
              ${contentCSS(theme)};
              overflow-y: auto;
            `}
          >
            <Dialog.Close css={closeButtonCSS}>
              <CloseSVG />
            </Dialog.Close>
            <div
              css={css`
                display: flex;
                justify-content: center;
                align-items: center;
                gap: 0.25em;
                font-size: 1.25em;
                font-weight: bold;
              `}
            >
              <WarningSVG />
              <span>Warning:</span>
              <span
                css={css`
                  font-weight: normal;
                `}
              >
                Delete a workspace
              </span>
            </div>
            <hr />

            <div
              css={css`
                display: flex;
                flex-direction: column;
                justify-content: center;
                align-items: center;
              `}
            >
              <p>You're about to delete a workspace.</p>

              {!haveUsersOrMaps && (
                <p>
                  Before you proceed, please note the following changes will
                  take place:
                  <br />
                  <ul
                    css={css`
                      font-weight: bold;
                      text-align: left;
                    `}
                  >
                    <li>Your workspace will be deleted.</li>
                    <li>
                      All maps will be moved to your main organisation page.
                    </li>
                    <li>
                      Any additional workspace users or guests will also be
                      merge to the your main organization.{' '}
                    </li>
                  </ul>
                </p>
              )}

              {haveUsersOrMaps && (
                <>
                  <p
                    css={css`
                      text-align: center;
                    `}
                  >
                    Before you proceed, you must migrate users and maps to
                    another workspace. Which workspace you want to migrate them
                    to?
                  </p>
                  <WorkspaceSelect
                    placement="top"
                    onChange={(workspace) => {
                      setSelectedWorkspace(workspace);
                    }}
                    placeholder="Select workspace"
                    containerStyles={{ width: '18em' }}
                    disabledOptions={[id]}
                  />
                  <br />
                </>
              )}
              <p
                css={css`
                  text-align: center;
                  margin: 0;
                `}
              >
                These changes are permanent and cannot be undone.
                <br />
                <br />
                <span
                  css={css`
                    font-weight: bold;
                  `}
                >
                  Are you sure you want to continue?
                </span>
              </p>

              <div css={buttonsHolderStyle}>
                <button type="button" css={cancelButtonstyle} onClick={cancel}>
                  <span>Cancel</span>
                </button>
                <button
                  type="button"
                  css={confirmButtonstyle}
                  onClick={confirm}
                  disabled={haveUsersOrMaps && !selectedWorkspace}
                >
                  {/* <span>{`Confirm${haveUsersOrMaps ? ' selection' : ''}`}</span> */}
                  <span>Yes, delete workspace</span>
                </button>
              </div>

              <div
                css={css`
                  margin-top: 2em;
                  text-align: center;
                `}
              >
                If you're having issues, contact support via chat from the
                dashboard or email{' '}
                <a css={blocks.linkPrimary} href="mailto:support@birdi.io">
                  support@birdi.io
                </a>
                .
              </div>
            </div>
          </Dialog.Content>
        </div>
      </Dialog.Portal>
    </Dialog.Root>
  );
};
